import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useReview } from '../../context/ReviewContext';
import ReviewCard from './ReviewCard';
import { reviewModel, reviewUtils } from '../../data/reviewModel';

const ReviewsPage = () => {
  const { type, id } = useParams(); // type: 'product' or 'store', id: productId or storeId
  const navigate = useNavigate();
  const { loadProductReviews, loadStoreReviews, getProductReviewStats, getStoreReviewStats } = useReview();
  const [reviews, setReviews] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filter, setFilter] = useState('all'); // all, 5, 4, 3, 2, 1

  useEffect(() => {
    if (type && id) {
      loadReviews();
    }
  }, [type, id, filter]);

  const loadReviews = async () => {
    if (!type || !id) return;

    setLoading(true);
    setError(null);

    try {
      let reviewsData = [];
      let statsData = null;

      if (type === 'product') {
        reviewsData = await loadProductReviews(id);
        statsData = await getProductReviewStats(id);
      } else if (type === 'store') {
        reviewsData = await loadStoreReviews(id);
        statsData = await getStoreReviewStats(id);
      }

      setReviews(reviewsData);
      setStats(statsData);
    } catch (error) {
      console.error('Error loading reviews:', error);
      setError('เกิดข้อผิดพลาดในการโหลดรีวิว');
    } finally {
      setLoading(false);
    }
  };

  const filteredReviews = reviews.filter(review => {
    if (filter === 'all') return true;
    return review.rating === parseInt(filter);
  });

  const getRatingDistribution = () => {
    if (!stats) return [];
    
    const total = stats.totalReviews || stats.total || 0;
    const distribution = [5, 4, 3, 2, 1].map(rating => ({
      rating,
      count: stats.ratingDistribution?.[rating] || 0,
      percentage: total > 0 
        ? ((stats.ratingDistribution?.[rating] || 0) / total * 100).toFixed(0)
        : 0
    }));

    return distribution;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-500 mx-auto"></div>
          <p className="mt-4 text-gray-600">กำลังโหลดรีวิว...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <p className="text-red-500 mb-4">{error}</p>
          <button
            onClick={() => navigate(-1)}
            className="px-4 py-2 bg-gray-500 text-white rounded-lg hover:bg-gray-600"
          >
            กลับ
          </button>
        </div>
      </div>
    );
  }

  const ratingDistribution = getRatingDistribution();

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="container mx-auto px-4 max-w-4xl">
        {/* Header */}
        <div className="mb-6">
          <button
            onClick={() => navigate(-1)}
            className="flex items-center text-gray-600 hover:text-gray-800 mb-4"
          >
            <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
            กลับ
          </button>
          <h1 className="text-3xl font-bold text-gray-800">รีวิว</h1>
        </div>

        {/* Review Stats */}
        {stats && (
          <div className="bg-white rounded-lg shadow-md p-6 mb-6">
            <div className="flex flex-col md:flex-row items-center md:items-start gap-6">
              {/* Average Rating */}
              <div className="text-center md:text-left">
                <div className="text-5xl font-bold text-gray-800 mb-2">
                  {stats.averageRating?.toFixed(1) || '0.0'}
                </div>
                <div className="flex items-center justify-center md:justify-start gap-1 mb-2">
                  {[1, 2, 3, 4, 5].map((star) => (
                    <span key={star} className="text-2xl">
                      {star <= Math.round(stats.averageRating || 0) ? '⭐' : '☆'}
                    </span>
                  ))}
                </div>
                <p className="text-gray-600">จาก {stats.totalReviews || stats.total || 0} รีวิว</p>
              </div>

              {/* Rating Distribution */}
              <div className="flex-1 w-full">
                <h3 className="text-sm font-semibold text-gray-700 mb-3">การกระจายคะแนน</h3>
                <div className="space-y-2">
                  {ratingDistribution.map(({ rating, count, percentage }) => (
                    <div key={rating} className="flex items-center gap-3">
                      <div className="w-12 text-right text-sm font-medium text-gray-700">
                        {rating} ⭐
                      </div>
                      <div className="flex-1 bg-gray-200 rounded-full h-6 relative overflow-hidden">
                        <div
                          className="bg-yellow-400 h-full rounded-full transition-all duration-300"
                          style={{ width: `${percentage}%` }}
                        />
                        <div className="absolute inset-0 flex items-center justify-center text-xs font-medium text-gray-700">
                          {count} ({percentage}%)
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Filter Tabs */}
        <div className="mb-6 flex gap-2 overflow-x-auto">
          <button
            onClick={() => setFilter('all')}
            className={`px-4 py-2 rounded-lg font-semibold whitespace-nowrap ${
              filter === 'all' 
                ? 'bg-green-500 text-white' 
                : 'bg-white text-gray-700 hover:bg-gray-100'
            }`}
          >
            ทั้งหมด ({reviews.length})
          </button>
          {[5, 4, 3, 2, 1].map((rating) => {
            const count = reviews.filter(r => r.rating === rating).length;
            return (
              <button
                key={rating}
                onClick={() => setFilter(rating.toString())}
                className={`px-4 py-2 rounded-lg font-semibold whitespace-nowrap ${
                  filter === rating.toString()
                    ? 'bg-yellow-500 text-white'
                    : 'bg-white text-gray-700 hover:bg-gray-100'
                }`}
              >
                {rating} ⭐ ({count})
              </button>
            );
          })}
        </div>

        {/* Reviews List */}
        {filteredReviews.length > 0 ? (
          <div className="space-y-4">
            {filteredReviews.map((review) => (
              <ReviewCard key={review.id} review={review} />
            ))}
          </div>
        ) : (
          <div className="bg-white rounded-lg shadow-md p-12 text-center">
            <p className="text-gray-500 text-lg">ไม่พบรีวิว</p>
            {filter !== 'all' && (
              <button
                onClick={() => setFilter('all')}
                className="mt-4 text-green-600 hover:text-green-700 underline"
              >
                ดูรีวิวทั้งหมด
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default ReviewsPage;

